import { OpenInNew } from '@mui/icons-material';
import { Backdrop, CircularProgress, Divider, FormHelperText, TextField, Typography, useTheme } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { enqueueSnackbar } from 'notistack';
import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { createProcedureQuickPick, getProcedureQuickPicks, updateProcedureQuickPick } from 'src/api/api';
import { AccordionCard } from 'src/components/AccordionCard';
import { useIsInlineFlow } from 'src/components/InlineFlow';
import { AutocompleteInput } from 'src/components/input/AutocompleteInput';
import { RoundedButton } from 'src/components/RoundedButton';
import { UnsavedDraftWarning } from 'src/components/UnsavedDraftWarning';
import { CPT_TOOLTIP_PROPS, TooltipWrapper } from 'src/components/WithTooltip';
import { dataTestIds } from 'src/constants/data-test-ids';
import { useApiClients } from 'src/hooks/useAppClients';
import { useCommandPaletteSource } from 'src/hooks/useCommandPaletteSource';
import useEvolveUser from 'src/hooks/useEvolveUser';
import { TRACKING_BOARD_QUERY_KEY } from 'src/hooks/useGetTrackingBoard';
import { sortQuickPicks, useMergedProcedureQuickPicks } from 'src/hooks/useMergedQuickPicks';
import { usePendingQuickPick } from 'src/hooks/usePendingQuickPick';
import { useDebounce } from 'src/shared/hooks/useDebounce';
import { useMarkDraftNavigatedAway, useProcedureStore } from 'src/state/draft-data.store';
import { PROCEDURES_CONFIG } from 'utils/lib/ottehr-config/procedures';
import { detectProcedureFamily } from 'utils/lib/procedure-coding/evaluate';
import { resolveFamilyFacts } from 'utils/lib/procedure-coding/family-support';
import { CodeOutcomeKind } from 'utils/lib/procedure-coding/model.types';
import { CPTCodeDTO, DiagnosisDTO } from 'utils/lib/types/api/chart-data/chart-data.types';
import { ProcedureQuickPickData } from 'utils/lib/types/api/quick-picks.types';
import { RoleType } from 'utils/lib/types/api/user.types';
import { PageTitle } from '../../shared/components/PageTitle';
import { QuickPicksButton } from '../../shared/components/QuickPicksButton';
import { useChartData } from '../../shared/hooks/useChartData';
import { useGetAppointmentAccessibility } from '../../shared/hooks/useGetAppointmentAccessibility';
import { useGetCPTHCPCSSearch } from '../../shared/stores/appointment/appointment.queries';
import {
  useAppointmentData,
  useDeleteChartData,
  useSaveChartData,
} from '../../shared/stores/appointment/appointment.store';
import { CodingAssistPanel } from '../components/procedures/coding-assist/CodingAssistPanel';
import { bestPracticeFindings, CodingFindingList } from '../components/procedures/coding-assist/CodingFindingList';
import { DocumentationCheck } from '../components/procedures/coding-assist/DocumentationCheck';
import { ConditionalCodingFields } from '../components/procedures/ConditionalCodingFields';
import { ProcedureFamilySentences } from '../components/procedures/narrative/CodingFieldSentences';
import {
  DateTimeBlank,
  MultiBlank,
  PopoverBlank,
  SectionLabel,
  SelectBlank,
  Sentence,
} from '../components/procedures/narrative/InlineBlanks';
import { ProcedureDiagnosesSentence } from '../components/procedures/narrative/ProcedureDiagnosesSentence';
import {
  DOCUMENTED_BY_OPTIONS,
  PERFORMED_BY_OPTIONS,
} from '../components/procedures/narrative/procedurePerformerOptions';
import { ProcedureCodingColumn } from '../components/procedures/ProcedureCodingColumn';
import { ProcedureCptCodesField } from '../components/procedures/ProcedureCptCodesField';
import { ProcedureQuickPickDialogs } from '../components/procedures/ProcedureQuickPickDialogs';
import { useProcedureSelectOptions } from '../components/procedures/useProcedureSelectOptions';
import { ProcedureCodingEvaluationStateKind, useProcedureCoding } from '../hooks/useProcedureCoding';
import { ROUTER_PATH } from '../routing/routesInPerson';
import { procedureFormResolver, ProcedureFormValues } from './procedureFormValidation';
import {
  combineMultipleValuesForSave,
  getPredefinedValueIfOther,
  getPredefinedValueOrOther,
  OTHER,
  parseWithOther,
} from './procedureOtherFields';
import {
  applyProcedurePrepopulation,
  initialProcedurePageState,
  LocalProcedurePageState,
  procedureFactsFromPageState,
  procedurePageStateToDraft,
} from './procedurePageState';
import {
  applyProcedureQuickPick,
  buildProcedureQuickPick,
  mergeProcedureCptCodes,
  sameCptLine,
} from './procedureQuickPick';

const SPECIMEN_SENT = ['sent', 'not sent'];
/** The coding column keeps its full width while the page has room, then gives up to 60px before the
 * two columns stack. 280px still fits the suggestion rows and the code search; the sentence form needs
 * about 420px (its button row is the widest piece) so it never gets squeezed below that. */
const CODING_COLUMN_WIDTH = 340;
const CODING_COLUMN_MIN_WIDTH = 280;
const FORM_COLUMN_MIN_WIDTH = 420;
const COLUMN_GAP_PX = 16;
/** Coding column beside the sentences once the page itself is wide enough, measured on the page rather
 * than the viewport so the visit sidebar (244px open, 56px collapsed), the scribe panel (440px by
 * default) and the progress-note inline flow are accounted for: the 716px threshold is about a 1000px
 * viewport with the sidebar open, and still holds on a 1440px laptop with the sidebar and panel open. */
const CODING_COLUMN_QUERY = `@container (min-width: ${
  FORM_COLUMN_MIN_WIDTH + COLUMN_GAP_PX + CODING_COLUMN_MIN_WIDTH
}px)`;
const CONSENT = ['obtained', 'not obtained'];

interface ProceduresNewProps {
  procedureId?: string;
  onFinished?: () => void;
}

export default function ProceduresNew({
  procedureId: procedureIdProp,
  onFinished,
}: ProceduresNewProps = {}): ReactElement {
  const navigate = useNavigate();
  const isInlineFlow = useIsInlineFlow();
  const theme = useTheme();
  const { id: appointmentId, procedureId: procedureIdFromUrl } = useParams();
  const procedureId = procedureIdProp ?? procedureIdFromUrl;
  const { oystehr, oystehrZambda } = useApiClients();
  const currentUser = useEvolveUser();
  const isAdmin = currentUser?.hasRole([RoleType.Administrator, RoleType.CustomerSupport]) ?? false;
  const { data: selectOptions, isLoading: isSelectOptionsLoading } = useProcedureSelectOptions(oystehr);
  const { chartData, setPartialChartData, chartDataSetState } = useChartData();
  const appointmentAccessibility = useGetAppointmentAccessibility();
  const queryClient = useQueryClient();

  const { encounter } = useAppointmentData();
  const { setDraft, getDraft, clearDraft, hasDraft } = useProcedureStore();
  useMarkDraftNavigatedAway({ encounterId: encounter.id ?? '', setDraft, hasDraft });
  const draft = !procedureId && encounter.id ? getDraft(encounter.id) : {};

  const isReadOnly = appointmentAccessibility.isAppointmentReadOnly;

  const chartCptCodes = chartData?.cptCodes || [];
  const chartDiagnoses = useMemo(() => chartData?.diagnosis ?? [], [chartData?.diagnosis]);
  const chartProcedures = chartData?.procedures || [];
  const { mutateAsync: saveChartData } = useSaveChartData();
  const { mutateAsync: deleteChartData } = useDeleteChartData();

  const methods = useForm<ProcedureFormValues>({
    defaultValues: draft.procedureType ? { procedureType: draft.procedureType } : undefined,
    resolver: procedureFormResolver,
  });
  const formValues = methods.watch();
  const {
    formState: { errors },
  } = methods;

  const [state, setState] = useState<LocalProcedurePageState>(() => initialProcedurePageState(draft));
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);
  const [quickPickDialogOpen, setQuickPickDialogOpen] = useState(false);
  const [quickPickName, setQuickPickName] = useState('');
  const [existingQuickPicks, setExistingQuickPicks] = useState<ProcedureQuickPickData[]>([]);
  const [quickPickSaving, setQuickPickSaving] = useState(false);
  const { quickPicks: mergedQuickPicks, loading: mergedQuickPicksLoading } = useMergedProcedureQuickPicks();

  const sortedMergedQuickPicks = useMemo(
    () => [...mergedQuickPicks].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
    [mergedQuickPicks]
  );

  const previousProcedureType = useRef<string | undefined>(draft.procedureType);
  const persistDraftAfterStateChangeRef = useRef(false);

  const updateState = useCallback((stateMutator: (draft: LocalProcedurePageState) => void): void => {
    persistDraftAfterStateChangeRef.current = true;
    setState((prev) => {
      const next = { ...prev };
      stateMutator(next);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!persistDraftAfterStateChangeRef.current) return;

    persistDraftAfterStateChangeRef.current = false;

    if (!procedureId && encounter.id) {
      setDraft(encounter.id, procedurePageStateToDraft(state));
    }
  }, [encounter.id, procedureId, setDraft, state]);

  const handleClearForm = (): void => {
    persistDraftAfterStateChangeRef.current = false;
    if (encounter.id) clearDraft(encounter.id);
    setState({ procedureDate: DateTime.now(), procedureTime: DateTime.now() });
    previousProcedureType.current = undefined;
    methods.reset({ procedureType: '' });
  };

  const parseSuppliesUsed = (
    rawValue: string | undefined,
    validOptions: string[] | undefined
  ): { suppliesUsed: string[]; otherSuppliesUsed?: string } => {
    const { values, other } = parseWithOther(rawValue, validOptions);
    return { suppliesUsed: values, otherSuppliesUsed: other };
  };

  const parsePostInstructions = (
    rawValue: string | undefined,
    validOptions: string[] | undefined
  ): { postInstructions: string[]; otherPostInstructions?: string } => {
    const { values, other } = parseWithOther(rawValue, validOptions);
    return { postInstructions: values, otherPostInstructions: other };
  };

  const procedureFacts = useMemo(() => {
    const procedures = chartData?.procedures ?? [];
    const assignedIds = new Set(
      [...procedures.flatMap((other) => other.cptCodes ?? []), ...(state.cptCodes ?? [])]
        .map((line) => line.resourceId)
        .filter(Boolean)
    );
    const otherProcedures = procedures
      .filter(
        (other) =>
          other.procedureDateTime &&
          state.procedureDate &&
          DateTime.fromISO(other.procedureDateTime).hasSame(state.procedureDate, 'day')
      )
      .map((other) => ({ procedureId: other.resourceId ?? '', codes: other.cptCodes ?? [] }));
    // Include visit-level codes (for example E/M), counting each linked billing resource once.
    if (
      encounter.period?.start &&
      state.procedureDate &&
      DateTime.fromISO(encounter.period.start).hasSame(state.procedureDate, 'day')
    ) {
      otherProcedures.push({
        procedureId: 'unassigned-visit-codes',
        codes: (chartData?.cptCodes ?? []).filter((line) => line.resourceId && !assignedIds.has(line.resourceId)),
      });
    }
    return {
      ...procedureFactsFromPageState(state, formValues.procedureType),
      procedureId,
      // The chart API contains this visit only. Never assert complete patient-day coverage.
      context: { completeDay: false, otherProcedures },
    };
  }, [
    formValues.procedureType,
    state,
    procedureId,
    chartData?.procedures,
    chartData?.cptCodes,
    encounter.period?.start,
  ]);

  const codingFamily = detectProcedureFamily(procedureFacts);
  const resolvedStructuredFacts = useMemo(
    () => (codingFamily ? resolveFamilyFacts(codingFamily, procedureFacts) : state.structuredFacts),
    [codingFamily, procedureFacts, state.structuredFacts]
  );
  const codingAssist = useProcedureCoding(procedureFacts);

  const codingEvaluations =
    codingAssist.evaluationState.kind === ProcedureCodingEvaluationStateKind.Ready
      ? codingAssist.evaluationState.current
      : codingAssist.evaluationState.previous;

  const codingAssistIsEvaluating = codingAssist.evaluationState.kind === ProcedureCodingEvaluationStateKind.Evaluating;

  const [initialValuesSet, setInitialValuesSet] = useState<boolean>(false);
  const [initialFormStateSet, setInitialFormStateSet] = useState<boolean>(false);
  const procedure = chartData?.procedures?.find((procedure) => procedure.resourceId === procedureId);

  useEffect(() => {
    if (procedure == null || initialValuesSet || selectOptions == null) {
      return;
    }

    const procedureDateTime =
      procedure.procedureDateTime != null ? DateTime.fromISO(procedure.procedureDateTime) : undefined;

    const parsedSupplies = parseSuppliesUsed(procedure.suppliesUsed, selectOptions?.supplies);

    const parsedPostInstructions = parsePostInstructions(
      procedure.postInstructions,
      selectOptions?.postProcedureInstructions
    );

    setState({
      cptCodes: procedure.cptCodes,
      diagnoses: procedure.diagnoses,
      procedureDate: procedureDateTime,
      procedureTime: procedureDateTime,
      performerType: procedure.performerType,
      medicationUsed: procedure.medicationUsed,
      bodySite: getPredefinedValueOrOther(procedure.bodySite, selectOptions?.bodySites),
      otherBodySite: getPredefinedValueIfOther(procedure.bodySite, selectOptions?.bodySites),
      bodySide: procedure.bodySide,
      technique: procedure.technique,
      suppliesUsed: parsedSupplies.suppliesUsed,
      otherSuppliesUsed: parsedSupplies.otherSuppliesUsed,
      procedureDetails: procedure.procedureDetails,
      structuredFacts: procedure.structuredFacts,
      lengthCm: procedure.lengthCm,
      repairDepth: procedure.repairDepth,
      infusionStartTime: procedure.infusionStartTime,
      infusionStopTime: procedure.infusionStopTime,
      specimenSent: procedure.specimenSent,
      complications: getPredefinedValueOrOther(procedure.complications, selectOptions?.complications),
      otherComplications: getPredefinedValueIfOther(procedure.complications, selectOptions?.complications),
      patientResponse: procedure.patientResponse,
      postInstructions: parsedPostInstructions.postInstructions,
      otherPostInstructions: parsedPostInstructions.otherPostInstructions,
      timeSpent: procedure.timeSpent,
      documentedBy: procedure.documentedBy,
      consentObtained: procedure.consentObtained,
    });

    previousProcedureType.current = procedure.procedureType;
    setInitialValuesSet(true);
  }, [procedure, setState, initialValuesSet, selectOptions]);

  const onCancel = (): void => {
    if (!procedureId && encounter.id) clearDraft(encounter.id);

    if (onFinished) onFinished();
    else navigate(`/in-person/${appointmentId}/${ROUTER_PATH.PROCEDURES}`);
  };

  const onSave = async (): Promise<void> => {
    setSaveInProgress(true);

    try {
      const cptCodesToSave =
        state.cptCodes?.filter((cptCode) => {
          if (cptCode.resourceId == null) return true;
          const original = chartCptCodes.find((chartCode) => chartCode.resourceId === cptCode.resourceId);
          return original == null || original.billableUnits !== cptCode.billableUnits;
        }) ?? [];

      // Drafts and retries may still carry code-only diagnoses that have since been charted.
      const visitDiagnosesByCode = new Map<string, (typeof chartDiagnoses)[number]>();
      for (const diagnosis of chartDiagnoses) {
        if (!visitDiagnosesByCode.has(diagnosis.code)) visitDiagnosesByCode.set(diagnosis.code, diagnosis);
      }
      const selectedDiagnoses = (state.diagnoses ?? []).map((diagnosis) =>
        diagnosis.resourceId ? diagnosis : visitDiagnosesByCode.get(diagnosis.code) ?? diagnosis
      );
      const saveCptAndDiagnosesResponse = await saveChartData({
        cptCodes: cptCodesToSave,
        diagnosis: selectedDiagnoses
          .filter((diagnosis) => diagnosis.resourceId == null)
          .map((diagnosis) => ({ ...diagnosis, addedViaProcedure: true })),
      });

      const savedCptCodes = saveCptAndDiagnosesResponse.chartData?.cptCodes;

      if (savedCptCodes) {
        const savedCptCodeIds = new Set(savedCptCodes.flatMap((code) => (code.resourceId ? [code.resourceId] : [])));
        setPartialChartData({
          cptCodes: [
            ...chartCptCodes.filter((code) => !code.resourceId || !savedCptCodeIds.has(code.resourceId)),
            ...savedCptCodes,
          ],
        });
      }

      const savedDiagnoses = saveCptAndDiagnosesResponse.chartData?.diagnosis;
      const updateChartDiagnoses = (deletedIds: readonly string[] = []): void => {
        const deleted = new Set(deletedIds);
        chartDataSetState(({ chartData }) => {
          if (!chartData) return {};
          const diagnosis: DiagnosisDTO[] = [];
          const indices = new Map<string, number>();
          for (const entry of [...(chartData.diagnosis ?? []), ...(savedDiagnoses ?? [])]) {
            if (entry.resourceId && deleted.has(entry.resourceId)) continue;
            const index = entry.resourceId ? indices.get(entry.resourceId) : undefined;
            if (index === undefined) {
              if (entry.resourceId) indices.set(entry.resourceId, diagnosis.length);
              diagnosis.push(entry);
            } else {
              diagnosis[index] = entry;
            }
          }
          return { chartData: { ...chartData, diagnosis } };
        });
      };

      if (savedDiagnoses) {
        updateChartDiagnoses();
      }

      const savedCptCodeIds = new Set(savedCptCodes?.flatMap((code) => (code.resourceId ? [code.resourceId] : [])));

      const cptCodesToUse = [
        ...(savedCptCodes ?? []),
        ...(state.cptCodes?.filter(
          (cptCode) => cptCode.resourceId != null && !savedCptCodeIds.has(cptCode.resourceId)
        ) ?? []),
      ];

      const diagnosesToUse = [
        ...(savedDiagnoses ?? []),
        ...selectedDiagnoses.filter((diagnosis) => diagnosis.resourceId != null),
      ];

      const oldProcedure = chartData?.procedures?.find((procedure) => procedure.resourceId === procedureId);
      const saveProcedureResponse = await saveChartData({
        cleanupProcedureDiagnoses: Boolean(
          oldProcedure?.diagnoses?.some(
            (diagnosis) => !diagnosesToUse.some((selected) => selected.resourceId === diagnosis.resourceId)
          )
        ),
        procedures: [
          {
            resourceId: procedureId,
            procedureType: formValues.procedureType,
            cptCodes: cptCodesToUse,
            diagnoses: diagnosesToUse,
            procedureDateTime: state.procedureDate
              ?.set({ hour: state.procedureTime?.hour, minute: state.procedureTime?.minute })
              ?.toUTC()
              ?.toString(),
            documentedDateTime: DateTime.now().toUTC().toString(),
            performerType: state.performerType,
            medicationUsed: state.medicationUsed,
            bodySite: state.bodySite !== OTHER ? state.bodySite : state.otherBodySite?.trim() || OTHER,
            bodySide: state.bodySide,
            technique: state.technique,
            suppliesUsed: combineMultipleValuesForSave(state.suppliesUsed, state.otherSuppliesUsed),
            procedureDetails: state.procedureDetails,
            structuredFacts: resolvedStructuredFacts,
            lengthCm: state.lengthCm,
            repairDepth: state.repairDepth,
            infusionStartTime: state.infusionStartTime,
            infusionStopTime: state.infusionStopTime,
            specimenSent: state.specimenSent,
            complications:
              state.complications !== OTHER ? state.complications : state.otherComplications?.trim() || OTHER,
            patientResponse: state.patientResponse,
            postInstructions: combineMultipleValuesForSave(state.postInstructions, state.otherPostInstructions),
            timeSpent: state.timeSpent,
            documentedBy: state.documentedBy,
            consentObtained: state.consentObtained,
          },
        ],
      });

      if (saveProcedureResponse.deletedDiagnosisIds?.length) {
        updateChartDiagnoses(saveProcedureResponse.deletedDiagnosisIds);
      }

      const removedCptCodes = oldProcedure?.cptCodes?.filter(
        (cptCode) => !cptCodesToUse.some((selected) => selected.resourceId === cptCode.resourceId)
      );
      if (removedCptCodes?.length) await deleteChartData({ cptCodes: removedCptCodes });

      const savedProcedure = saveProcedureResponse.chartData?.procedures?.[0];

      if (savedProcedure) {
        setPartialChartData({
          procedures: [
            ...chartProcedures.filter((procedure) => procedure.resourceId !== procedureId),
            {
              ...savedProcedure,
              cptCodes: cptCodesToUse,
              diagnoses: diagnosesToUse,
            },
          ],
        });
      }

      void queryClient.invalidateQueries({
        queryKey: [TRACKING_BOARD_QUERY_KEY],
        refetchType: 'active',
      });

      setSaveInProgress(false);
      enqueueSnackbar('Procedure saved!', { variant: 'success' });

      if (!procedureId && encounter.id) clearDraft(encounter.id);

      if (onFinished) {
        onFinished();
      } else {
        navigate(`/in-person/${appointmentId}/${ROUTER_PATH.PROCEDURES}`);
      }
    } catch {
      setSaveInProgress(false);
      enqueueSnackbar('An error has occurred while saving procedure. Please try again.', { variant: 'error' });
    }
  };

  const openQuickPickDialog = async (): Promise<void> => {
    if (!oystehrZambda) return;

    try {
      const response = await getProcedureQuickPicks(oystehrZambda);
      setExistingQuickPicks([...response.quickPicks].sort(sortQuickPicks));
    } catch (error) {
      console.error('Failed to load existing quick picks:', error);
      setExistingQuickPicks(sortedMergedQuickPicks);
    }

    const parts: string[] = [];

    if (formValues.procedureType) parts.push(formValues.procedureType);

    if (state.bodySite) parts.push(state.bodySite);

    if (state.bodySide) parts.push(state.bodySide);

    if (state.complications) parts.push(state.complications);

    if (state.cptCodes?.length) parts.push(state.cptCodes.map((c) => c.code).join(', '));

    setQuickPickName(parts.join(' | '));
    setQuickPickDialogOpen(true);
  };

  const buildQuickPickFromCurrentState = (): Omit<ProcedureQuickPickData, 'id'> => {
    return buildProcedureQuickPick(
      {
        ...state,
        structuredFacts: resolvedStructuredFacts,
      },
      quickPickName,
      formValues.procedureType,
      selectOptions?.procedureTypes
    );
  };

  const onSaveAsQuickPick = async (overwriteId?: string): Promise<void> => {
    if (!quickPickName.trim()) {
      enqueueSnackbar('Quick pick name is required', { variant: 'error' });

      return;
    }

    if (!oystehrZambda) {
      throw new Error('oystehrZambda was null');
    }

    setQuickPickSaving(true);

    try {
      const quickPickData = buildQuickPickFromCurrentState();

      if (overwriteId) {
        await updateProcedureQuickPick(oystehrZambda, overwriteId, quickPickData);
        enqueueSnackbar(`Quick pick "${quickPickName}" updated`, { variant: 'success' });
      } else {
        await createProcedureQuickPick(oystehrZambda, { quickPick: quickPickData });
        enqueueSnackbar(`Quick pick "${quickPickName}" created`, { variant: 'success' });
      }

      setQuickPickDialogOpen(false);
    } catch (error) {
      console.error('Failed to save quick pick:', error);
      enqueueSnackbar('Failed to save quick pick', { variant: 'error' });
    } finally {
      setQuickPickSaving(false);
    }
  };

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const { isFetching: isSearching, data } = useGetCPTHCPCSSearch({ search: debouncedSearchTerm, type: 'both' });
  const cptSearchOptions = (data as { codes?: CPTCodeDTO[] })?.codes || [];
  const { debounce } = useDebounce(800);

  const debouncedHandleInputChange = (data: string): void => {
    debounce(() => {
      setDebouncedSearchTerm(data);
    });
  };

  const addRecommendedCptCodes = (entries: CPTCodeDTO[]): void =>
    updateState((state) => {
      state.cptCodes = mergeProcedureCptCodes(state.cptCodes, entries);
    });

  const forwardEvaluation = codingEvaluations?.suggestion;

  const codingGuidance = bestPracticeFindings(forwardEvaluation?.findings ?? []);

  const suggestionVisible =
    forwardEvaluation?.outcome?.kind === CodeOutcomeKind.Suggestions ||
    forwardEvaluation?.outcome?.kind === CodeOutcomeKind.Determined ||
    forwardEvaluation?.outcome?.kind === CodeOutcomeKind.DeterminedWithAlternates ||
    forwardEvaluation?.outcome?.kind === CodeOutcomeKind.Open;

  useEffect(() => {
    if (procedureId && !initialFormStateSet) {
      return;
    }

    const callback = methods.subscribe({
      name: 'procedureType',
      formState: {
        values: true,
      },
      callback: ({ values }) => {
        // Empty procedure type (e.g. "Clear Form" reset) must not touch page state
        if (!values.procedureType) return;

        const previousName = previousProcedureType.current;
        previousProcedureType.current = values.procedureType;

        if (
          previousName !== undefined &&
          detectProcedureFamily({ procedureType: previousName })?.id !==
            detectProcedureFamily({ procedureType: values.procedureType })?.id
        ) {
          updateState((state) => {
            state.structuredFacts = undefined;
          });
        }

        if (!procedureId && encounter.id) {
          setDraft(encounter.id, { procedureType: values.procedureType });
        }

        updateState((state) => {
          const selected = selectOptions?.procedureTypes.find(
            (procedureType) => procedureType.name === values.procedureType
          );

          if (selected) {
            applyProcedurePrepopulation(state, PROCEDURES_CONFIG.prepopulation[selected.code] ?? {});
          }
        });
      },
    });

    return () => callback();
  }, [methods, selectOptions, procedureId, initialFormStateSet, encounter.id, setDraft, updateState]);

  useEffect(() => {
    if (procedure == null) {
      return;
    }

    methods.reset({
      procedureType: procedure.procedureType,
    });

    setInitialFormStateSet(true);
  }, [methods, procedure]);

  const onQuickPickSelect = (quickPick: ProcedureQuickPickData): void => {
    const procedureTypeName = quickPick.procedureType
      ? selectOptions?.procedureTypes.find((procedureType) => procedureType.code === quickPick.procedureType)?.name ??
        quickPick.procedureType
      : undefined;

    if (procedureTypeName) {
      methods.reset({ ...formValues, procedureType: procedureTypeName });
    }

    updateState((state) => {
      applyProcedureQuickPick(state, quickPick, procedureTypeName ?? formValues.procedureType);
    });
  };

  const onQuickPickSelectRef = useRef(onQuickPickSelect);
  onQuickPickSelectRef.current = onQuickPickSelect;

  const commandPaletteItems = useMemo(
    () =>
      procedureId || isReadOnly || selectOptions == null
        ? []
        : mergedQuickPicks.map((quickPick) => ({
            id: `procedure-${quickPick.id ?? quickPick.name}`,
            label: quickPick.name,
            category: 'Add Procedure',
            onSelect: () => onQuickPickSelectRef.current(quickPick),
          })),
    [isReadOnly, mergedQuickPicks, procedureId, selectOptions]
  );
  useCommandPaletteSource('procedure-quick-picks', commandPaletteItems);

  const handlePendingQuickPick = useCallback((payload: ProcedureQuickPickData) => {
    onQuickPickSelectRef.current(payload);
  }, []);

  usePendingQuickPick('procedures', handlePendingQuickPick, selectOptions != null);

  const [consentPdfExists, setConsentPdfExists] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch('/consent_procedure.pdf', { method: 'HEAD' })
      .then((res) => {
        const contentType = res.headers.get('content-type') ?? '';
        if (!cancelled) setConsentPdfExists(res.ok && contentType.includes('pdf'));
      })
      .catch(() => {
        if (!cancelled) setConsentPdfExists(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <FormProvider {...methods}>
      <Stack spacing={1}>
        {!isInlineFlow && (
          <PageTitle
            label="Document Procedure"
            showIntakeNotesButton={false}
            dataTestId={dataTestIds.documentProcedurePage.title}
          />
        )}
        {!procedureId && hasDraft(encounter.id ?? '') && (
          <UnsavedDraftWarning
            message={
              draft.hasNavigatedAway
                ? 'Your previously entered data has been restored. Click "Clear Form" to start fresh.'
                : 'You have a procedure in progress. Your draft will be saved.'
            }
          />
        )}
        <Box sx={{ containerType: 'inline-size' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr)',
              gap: `${COLUMN_GAP_PX}px`,
              alignItems: 'start',
              [CODING_COLUMN_QUERY]: {
                gridTemplateColumns: `minmax(${FORM_COLUMN_MIN_WIDTH}px, 1fr) minmax(${CODING_COLUMN_MIN_WIDTH}px, ${CODING_COLUMN_WIDTH}px)`,
              },
            }}
          >
            <AccordionCard>
              <Stack spacing={1.5} style={{ padding: '24px' }}>
                <QuickPicksButton
                  quickPicks={sortedMergedQuickPicks}
                  loading={mergedQuickPicksLoading}
                  disabled={selectOptions == null}
                  getLabel={(quickPick) => quickPick.name}
                  onSelect={onQuickPickSelect}
                  showAddOption
                  isAdmin={isAdmin}
                  onAddOrUpdate={() => void openQuickPickDialog()}
                  searchable
                />

                <SectionLabel>Procedure</SectionLabel>
                <Sentence>
                  <PopoverBlank
                    label="procedure type"
                    title="Procedure type"
                    value={formValues.procedureType || undefined}
                    need
                    readOnly={isReadOnly}
                    dataTestId={dataTestIds.documentProcedurePage.procedureType}
                  >
                    {(close) => (
                      <AutocompleteInput
                        name="procedureType"
                        label="Procedure type"
                        options={selectOptions?.procedureTypes.map((procedureType) => procedureType.name)}
                        disabled={isReadOnly}
                        loading={isSelectOptionsLoading}
                        freeSolo
                        dataTestId={dataTestIds.documentProcedurePage.procedureTypeInput}
                        // Clearing the text reports a null option; only a real pick closes the popover.
                        onOptionSelected={(option) => option != null && close()}
                        openOnFocus
                      />
                    )}
                  </PopoverBlank>
                  {' on '}
                  <DateTimeBlank
                    kind="date"
                    label="date of the procedure"
                    value={state.procedureDate}
                    onChange={(date) => updateState((state) => (state.procedureDate = date))}
                    readOnly={isReadOnly}
                    dataTestId={dataTestIds.documentProcedurePage.procedureDate}
                  />
                  {' at '}
                  <DateTimeBlank
                    kind="time"
                    label="time of the procedure"
                    value={state.procedureTime}
                    onChange={(time) => updateState((state) => (state.procedureTime = time))}
                    readOnly={isReadOnly}
                    dataTestId={dataTestIds.documentProcedurePage.procedureTime}
                  />
                  {', performed by the '}
                  <SelectBlank
                    label="performed by"
                    title="Performed by"
                    options={PERFORMED_BY_OPTIONS}
                    value={state.performerType}
                    onChange={(value) => updateState((state) => (state.performerType = value))}
                    readOnly={isReadOnly}
                    dataTestId={dataTestIds.documentProcedurePage.performedBy}
                  />
                  {' and documented by the '}
                  <SelectBlank
                    label="documented by"
                    title="Documented by"
                    options={DOCUMENTED_BY_OPTIONS}
                    value={state.documentedBy}
                    onChange={(value) => updateState((state) => (state.documentedBy = value))}
                    readOnly={isReadOnly}
                    dataTestId={dataTestIds.documentProcedurePage.documentedBy}
                  />
                  .
                </Sentence>
                {errors.procedureType && (
                  <FormHelperText error sx={{ mt: '-8px' }}>
                    {String(errors.procedureType.message)}
                  </FormHelperText>
                )}
                <ProcedureDiagnosesSentence
                  diagnoses={state.diagnoses ?? []}
                  visitDiagnoses={chartDiagnoses}
                  readOnly={isReadOnly}
                  onChange={(diagnoses) => updateState((state) => (state.diagnoses = diagnoses))}
                />
                <Sentence>
                  Consent for Procedure{' '}
                  {consentPdfExists && (
                    <Link
                      target="_blank"
                      to={`/consent_procedure.pdf`}
                      aria-label="Open the Consent for Procedure form"
                      title="Open the Consent for Procedure form"
                      style={{ color: theme.palette.text.secondary, lineHeight: 0 }}
                    >
                      <OpenInNew sx={{ fontSize: 16, verticalAlign: 'text-bottom' }} />
                    </Link>
                  )}{' '}
                  <SelectBlank
                    label="consent"
                    title="Consent for Procedure"
                    options={CONSENT}
                    value={state.consentObtained == null ? undefined : state.consentObtained ? CONSENT[0] : CONSENT[1]}
                    onChange={(value) =>
                      updateState(
                        (state) => (state.consentObtained = value === undefined ? undefined : value === CONSENT[0])
                      )
                    }
                    readOnly={isReadOnly}
                    need
                    dataTestId={dataTestIds.documentProcedurePage.consentForProcedure}
                  />
                  .
                </Sentence>

                {(!codingFamily?.capturesSite || !codingFamily?.capturesSide) && (
                  <>
                    <SectionLabel>Site</SectionLabel>
                    <Sentence>
                      Site:{' '}
                      {!codingFamily?.capturesSite && (
                        <SelectBlank
                          label="site/location"
                          title="Site/location"
                          options={selectOptions?.bodySites}
                          value={state.bodySite}
                          otherValue={state.otherBodySite}
                          onOtherChange={(value) => updateState((state) => (state.otherBodySite = value))}
                          onChange={(value) =>
                            updateState((state) => {
                              state.bodySite = value;
                              state.otherBodySite = undefined;
                            })
                          }
                          readOnly={isReadOnly}
                          clearable
                          dataTestId={dataTestIds.documentProcedurePage.site}
                        />
                      )}
                      {!codingFamily?.capturesSite && !codingFamily?.capturesSide && ' '}
                      {!codingFamily?.capturesSide && (
                        <>
                          (
                          <SelectBlank
                            label="side"
                            title="Side of body"
                            options={selectOptions?.bodySides}
                            value={state.bodySide}
                            onChange={(value) => updateState((state) => (state.bodySide = value))}
                            readOnly={isReadOnly}
                            clearable
                            dataTestId={dataTestIds.documentProcedurePage.sideOfBody}
                          />
                          )
                        </>
                      )}
                      .
                    </Sentence>
                  </>
                )}

                {codingFamily && (
                  <>
                    <SectionLabel>{codingFamily.displayName}</SectionLabel>
                    <ProcedureFamilySentences
                      family={codingFamily}
                      value={resolvedStructuredFacts ?? {}}
                      onChange={(value) => updateState((state) => (state.structuredFacts = value))}
                      readOnly={isReadOnly}
                      medicationUsed={state.medicationUsed}
                    />
                  </>
                )}
                <ConditionalCodingFields
                  visibility={
                    codingFamily
                      ? { length: false, repairDepth: false, infusionTimes: false }
                      : codingAssist.fieldVisibility
                  }
                  isReadOnly={isReadOnly}
                  lengthCm={state.lengthCm}
                  repairDepth={state.repairDepth}
                  infusionStartTime={state.infusionStartTime}
                  infusionStopTime={state.infusionStopTime}
                  onLengthChange={(value) => updateState((state) => (state.lengthCm = value))}
                  onRepairDepthChange={(value) => updateState((state) => (state.repairDepth = value))}
                  onInfusionStartChange={(value) => updateState((state) => (state.infusionStartTime = value))}
                  onInfusionStopChange={(value) => updateState((state) => (state.infusionStopTime = value))}
                />

                <SectionLabel>Technique</SectionLabel>
                <Sentence>
                  Anesthesia:{' '}
                  <SelectBlank
                    label="anesthesia"
                    title="Anaesthesia / medication used"
                    options={selectOptions?.medicationsUsed}
                    value={state.medicationUsed}
                    onChange={(value) => updateState((state) => (state.medicationUsed = value))}
                    readOnly={isReadOnly}
                    clearable
                    dataTestId={dataTestIds.documentProcedurePage.anaesthesia}
                  />
                  . Technique:{' '}
                  <MultiBlank
                    label="technique"
                    title="Technique"
                    options={selectOptions?.techniques}
                    values={state.technique}
                    onChange={(values) => updateState((state) => (state.technique = values))}
                    readOnly={isReadOnly}
                    dataTestId={dataTestIds.documentProcedurePage.technique}
                  />
                  . Supplies:{' '}
                  <MultiBlank
                    label="supplies"
                    title="Instruments / supplies used"
                    options={selectOptions?.supplies}
                    values={state.suppliesUsed}
                    otherValue={state.otherSuppliesUsed}
                    onOtherChange={(value) => updateState((state) => (state.otherSuppliesUsed = value))}
                    onChange={(values) => updateState((state) => (state.suppliesUsed = values))}
                    readOnly={isReadOnly}
                    dataTestId={dataTestIds.documentProcedurePage.instruments}
                  />
                  .
                </Sentence>

                <SectionLabel>Outcome</SectionLabel>
                <Sentence>
                  Specimen{' '}
                  <SelectBlank
                    label="specimen"
                    title="Specimen sent"
                    options={SPECIMEN_SENT}
                    value={
                      state.specimenSent == null ? undefined : state.specimenSent ? SPECIMEN_SENT[0] : SPECIMEN_SENT[1]
                    }
                    onChange={(value) =>
                      updateState(
                        (state) => (state.specimenSent = value === undefined ? undefined : value === SPECIMEN_SENT[0])
                      )
                    }
                    readOnly={isReadOnly}
                    dataTestId={dataTestIds.documentProcedurePage.specimenSent}
                  />
                  . Complications:{' '}
                  <SelectBlank
                    label="complications"
                    title="Complications"
                    options={selectOptions?.complications}
                    value={state.complications}
                    otherValue={state.otherComplications}
                    onOtherChange={(value) => updateState((state) => (state.otherComplications = value))}
                    onChange={(value) =>
                      updateState((state) => {
                        state.complications = value;
                        state.otherComplications = undefined;
                      })
                    }
                    readOnly={isReadOnly}
                    clearable
                    dataTestId={dataTestIds.documentProcedurePage.complications}
                  />
                  . Patient{' '}
                  <SelectBlank
                    label="response"
                    title="Patient response"
                    options={selectOptions?.patientResponses}
                    value={state.patientResponse}
                    onChange={(value) => updateState((state) => (state.patientResponse = value))}
                    readOnly={isReadOnly}
                    clearable
                    dataTestId={dataTestIds.documentProcedurePage.patientResponse}
                  />
                  . Time spent:{' '}
                  <SelectBlank
                    label="time spent"
                    title="Time spent"
                    options={selectOptions?.timeSpent}
                    value={state.timeSpent}
                    onChange={(value) => updateState((state) => (state.timeSpent = value))}
                    readOnly={isReadOnly}
                    clearable
                    dataTestId={dataTestIds.documentProcedurePage.timeSpent}
                  />
                  .
                </Sentence>
                <Sentence>
                  Instructions given:{' '}
                  <MultiBlank
                    label="instructions"
                    title="Post-procedure Instructions"
                    options={selectOptions?.postProcedureInstructions}
                    values={state.postInstructions}
                    otherValue={state.otherPostInstructions}
                    onOtherChange={(value) => updateState((state) => (state.otherPostInstructions = value))}
                    onChange={(values) => updateState((state) => (state.postInstructions = values))}
                    readOnly={isReadOnly}
                    dataTestId={dataTestIds.documentProcedurePage.postProcedureInstructions}
                  />
                  .
                </Sentence>

                <SectionLabel>Procedure details</SectionLabel>
                <TextField
                  placeholder="Anything the sentences above don't cover"
                  inputProps={{ 'aria-label': 'Procedure details' }}
                  multiline
                  rows={4}
                  value={state.procedureDetails ?? ''}
                  onChange={(e: any) => updateState((state) => (state.procedureDetails = e.target.value))}
                  disabled={isReadOnly}
                  data-testid={dataTestIds.documentProcedurePage.procedureDetails}
                />

                <Divider orientation="horizontal" />

                <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Stack direction="row" spacing={2}>
                    <RoundedButton color="primary" onClick={onCancel}>
                      Cancel
                    </RoundedButton>
                    {!procedureId && (
                      <RoundedButton color="primary" onClick={handleClearForm}>
                        Clear Form
                      </RoundedButton>
                    )}
                  </Stack>
                  <RoundedButton
                    color="primary"
                    variant="contained"
                    disabled={isReadOnly}
                    onClick={methods.handleSubmit(onSave)}
                    data-testid={dataTestIds.documentProcedurePage.saveButton}
                  >
                    Save
                  </RoundedButton>
                </Box>

                {Object.entries(errors).length > 0 && (
                  <FormHelperText sx={{ textAlign: 'right' }} error={true}>
                    Please fix all errors
                  </FormHelperText>
                )}
              </Stack>
            </AccordionCard>
            <ProcedureCodingColumn containerQuery={CODING_COLUMN_QUERY}>
              <AccordionCard>
                <Stack spacing={1.5} style={{ padding: '24px' }}>
                  <TooltipWrapper tooltipProps={CPT_TOOLTIP_PROPS}>
                    <Typography style={{ color: theme.palette.primary.dark, fontSize: '16px', fontWeight: '500' }}>
                      CPT Code
                    </Typography>
                  </TooltipWrapper>

                  <CodingAssistPanel
                    onRetrySuggestions={codingAssist.retrySuggestions}
                    evaluation={forwardEvaluation}
                    isEvaluating={codingAssistIsEvaluating}
                    procedureTypeSelected={Boolean(formValues.procedureType)}
                    isReadOnly={isReadOnly}
                    selectedCodes={state.cptCodes ?? []}
                    onAddCodes={addRecommendedCptCodes}
                  />

                  <DocumentationCheck evaluation={codingEvaluations?.defense} suggestionVisible={suggestionVisible} />

                  <ProcedureCptCodesField
                    codes={state.cptCodes ?? []}
                    searchOptions={cptSearchOptions}
                    isSearching={isSearching}
                    searchTerm={debouncedSearchTerm}
                    onSearchTermChange={debouncedHandleInputChange}
                    onAdd={(code) => updateState((state) => (state.cptCodes = [...(state.cptCodes ?? []), code]))}
                    onDelete={(code) =>
                      updateState((state) => {
                        // The same code can appear twice with different modifiers (a repeat recording, a payer
                        // alternative). Delete the line the user clicked, not every line sharing its code.
                        const index = state.cptCodes?.findIndex((cptCode) => sameCptLine(cptCode, code)) ?? -1;
                        if (index >= 0) state.cptCodes = state.cptCodes?.filter((_, i) => i !== index);
                      })
                    }
                    disabled={isReadOnly}
                  />

                  {codingGuidance.length > 0 && !codingAssistIsEvaluating && (
                    <Box sx={{ background: theme.palette.action.hover, borderRadius: '8px', padding: '8px' }}>
                      <CodingFindingList
                        findings={codingGuidance}
                        dataTestId={dataTestIds.documentProcedurePage.codingGuidance}
                      />
                    </Box>
                  )}
                </Stack>
              </AccordionCard>
            </ProcedureCodingColumn>
          </Box>
        </Box>
      </Stack>

      <Backdrop sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })} open={saveInProgress}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <ProcedureQuickPickDialogs
        open={quickPickDialogOpen}
        name={quickPickName}
        onNameChange={setQuickPickName}
        existingQuickPicks={existingQuickPicks}
        saving={quickPickSaving}
        onClose={() => setQuickPickDialogOpen(false)}
        onSave={(overwriteId) => void onSaveAsQuickPick(overwriteId)}
      />
    </FormProvider>
  );
}
